import React from "react";

const COLORS = {
  up:   "#9DD034",   // lifted green
  down: "#F87171",   // rose-400
  flat: "#94A3B8",   // slate-400
};

const ARROWS = { up: "▲", down: "▼", flat: "▬" };

/**
 * Pure-SVG sparkline. No chart library, no animation library — just a
 * polyline that scales [min..max] across the available width.
 *
 * Props:
 *   data:      number[]  (required) — sparkline points
 *   width:     number    px (default 80)
 *   height:    number    px (default 24)
 *   direction: "up"|"down"|"flat" (default "flat") — drives stroke colour
 */
export function Sparkline({ data = [], width = 80, height = 24, direction = "flat", testId }) {
  if (!Array.isArray(data) || data.length < 2) {
    return (
      <svg width={width} height={height} data-testid={testId} aria-hidden="true">
        <line x1={2} y1={height / 2} x2={width - 2} y2={height / 2}
              stroke={COLORS.flat} strokeWidth={1} strokeDasharray="2 2" opacity="0.5" />
      </svg>
    );
  }
  const min = Math.min(...data);
  const max = Math.max(...data);
  const span = max - min || 1;
  const stepX = (width - 4) / (data.length - 1);
  const pts = data.map((v, i) => {
    const x = 2 + i * stepX;
    const y = height - 2 - ((v - min) / span) * (height - 4);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");
  const stroke = COLORS[direction] || COLORS.flat;
  return (
    <svg width={width} height={height} data-testid={testId} aria-hidden="true">
      <polyline points={pts} fill="none" stroke={stroke} strokeWidth={1.5}
                strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/**
 * Direction pill — ▲/▼/▬ + signed percentage.
 *
 * Props:
 *   delta:     number   percentage change (signed)
 *   direction: "up"|"down"|"flat"
 */
export function TrendIndicator({ delta = 0, direction = "flat", testId }) {
  const colour = COLORS[direction] || COLORS.flat;
  const sign = delta > 0 ? "+" : "";
  return (
    <span
      className="inline-flex items-center gap-1 font-bebas tracking-[0.18em] text-[0.7rem]"
      style={{ color: colour }}
      data-testid={testId}
    >
      <span aria-hidden="true">{ARROWS[direction] || "▬"}</span>
      <span>{direction === "flat" ? "FLAT" : `${sign}${delta}%`}</span>
    </span>
  );
}
